import { useState, useEffect, useCallback } from "react";
import { apiClient } from "@/services/apiClient";
import type { DataHealthMap } from "@/types";

interface DataHealthResponse {
  clients: DataHealthMap;
}

export function useAllDataHealth() {
  const [data, setData] = useState<DataHealthMap>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchHealth = useCallback(async () => {
    try {
      const result = await apiClient.get("/api/social-media/data-health");
      setData((result as DataHealthResponse).clients || {});
      setError(null);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || "Failed to fetch data health");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHealth();
  }, [fetchHealth]);

  return { data, isLoading, error, refetch: fetchHealth };
}

export function useSyncAudienceActivity() {
  const [isSyncing, setIsSyncing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const sync = useCallback(async (clientId: string): Promise<boolean> => {
    setIsSyncing(clientId);
    setError(null);

    try {
      await apiClient.post(`/api/social-media/audience-activity/${clientId}/refresh`);
      return true;
    } catch (err: unknown) {
      const apiErr = err as { response?: { data?: { error?: string } }; message?: string };
      setError(apiErr.response?.data?.error || apiErr.message || "Sync failed");
      return false;
    } finally {
      setIsSyncing(null);
    }
  }, []);

  return { sync, isSyncing, error };
}
